import { motion } from 'framer-motion';
import { Crown, CreditCard, ShoppingBag, Newspaper, Megaphone } from 'lucide-react';
import type { ReactNode } from 'react';

type PremiumCard = {
  title: string;
  sentences: ReactNode[];
  icon: typeof CreditCard;
  verMasUrl?: string;
  comingSoon?: boolean;
};

const premiumCards: PremiumCard[] = [
  {
    title: 'Sistemas de pagos',
    sentences: [
      'La sección de Pagos está pensada para que una federación, liga o torneo pueda administrar de forma centralizada todos los movimientos económicos relacionados con la competencia.',
      'Gestioná inscripciones de clubes o equipos, cuotas mensuales, multas y sanciones económicas, derechos de pase u otros conceptos personalizados.',
      <strong key="pagos-highlight">
        Todo queda organizado y asociado al torneo y a cada participante.
      </strong>,
    ],
    icon: CreditCard,
    verMasUrl: 'https://aguanta.me/protorneos',
  },
  {
    title: 'Marketplace',
    sentences: [
      'Ofrecé productos y servicios desde tu club: paquetes en la cantina, merchandising oficial, tickets de estacionamiento y próximamente accesos digitales.',
      'Una nueva forma de generar ingresos y acercar valor a tu gente.',
    ],
    icon: ShoppingBag,
    comingSoon: true,
  },
  {
    title: 'Coberturas de prensa',
    sentences: [
      'Con la integración a Gol Popular, tu torneo gana visibilidad con notas periodísticas llenas de pasión y datos.',
      'Fotos exclusivas y contenido que refuerza la identidad de cada jugador y de los equipos.',
    ],
    icon: Newspaper,
    verMasUrl: 'https://www.golpopular.com/',
  },
  {
    title: 'Activaciones para sponsors',
    sentences: [
      'Llevá a tus sponsors al corazón de tu comunidad con experiencias interactivas: prodes, rifas, trivias y más.',
      'Ellos conectan con equipos e hinchas, vos potenciás tu torneo.',
    ],
    icon: Megaphone,
    comingSoon: true,
  },
];

export function PremiumSection() {
  return (
    <section
      id="premium"
      className="relative bg-gradient-to-b from-brand-yellow/10 via-brand-yellow/5 to-transparent px-4 py-12 sm:px-6 lg:px-8"
    >
      <div className="mx-auto w-full max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.45 }}
          className="text-center"
        >
          <h2 className="font-display text-4xl font-extrabold leading-tight sm:text-5xl">
            <Crown className="mx-auto mb-3 h-10 w-10 text-brand-yellow" strokeWidth={1.75} />
            PROTORNEOS PREMIUM
          </h2>

          <p className="mx-auto mt-4 max-w-2xl text-sm text-zinc-300 sm:text-base">
            Llevá la gestión de tu torneo al siguiente nivel con herramientas exclusivas diseñadas
            para organizadores que buscan más control y profesionalismo.
          </p>
        </motion.div>

        <div className="mt-10 grid w-full grid-cols-1 gap-5 sm:grid-cols-2">
          {premiumCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <motion.article
                key={card.title}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.38, delay: index * 0.07 }}
                className="flex min-h-full w-full flex-col rounded-2xl border border-brand-border bg-brand-panel/90 p-6 shadow-panel sm:p-7 lg:p-8"
              >
                <div className="mb-4 inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-brand-yellow/40 bg-brand-yellow/10 text-brand-yellow lg:h-14 lg:w-14">
                  <Icon className="h-6 w-6 lg:h-7 lg:w-7" strokeWidth={1.75} aria-hidden />
                </div>
                <h4 className="font-display text-xl font-bold leading-snug text-white lg:text-2xl">
                  {card.title}
                </h4>
                <div className="mt-3 flex flex-col gap-3 text-sm leading-relaxed text-zinc-300 lg:text-base">
                  {card.sentences.map((sentence, sentenceIndex) => (
                    <p key={`${card.title}-${sentenceIndex}`}>{sentence}</p>
                  ))}
                </div>
                {card.verMasUrl && (
                  <a
                    href={card.verMasUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 inline-flex w-fit items-center justify-center rounded-xl border border-brand-yellow/40 bg-brand-yellow/10 px-5 py-2.5 text-sm font-semibold text-brand-yellow transition-colors hover:bg-brand-yellow/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-yellow/60"
                  >
                    Saber más
                  </a>
                )}
                {card.comingSoon && (
                  <span className="mt-6 inline-flex w-fit items-center justify-center rounded-full border border-brand-yellow/60 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-brand-yellow">
                    Próximamente
                  </span>
                )}
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

